<script setup lang="ts">
import {
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  nextTick,
  useId,
  watch,
  type CSSProperties,
} from "vue";
import Icon from "./Icon.vue";
import { useEscapeLayer } from "../composables/escape";
import { t } from "../i18n";
const model = defineModel<string>({ default: "" });
/** With `multiple`: every option chosen, shown as chips under the box, each with its ×. */
const values = defineModel<string[]>("values", { default: () => [] });
const props = withDefaults(
  defineProps<{
    options: string[];
    label: string;
    disabled?: boolean;
    /** Force or suppress the search box; "auto" shows it when the list is long. */
    searchable?: boolean | "auto";
    /**
     * Inside a sentence ("Quando [o contato não responde ▾] por…"): a slim box as wide as its
     * value, without the floating label (still read by screen readers); the list is the same.
     */
    inline?: boolean;
    /** What the closed box shows for the chosen option ("🇧🇷 +55" for "🇧🇷 Brasil +55"). */
    display?: (option: string) => string;
    /** A list wider than a narrow box (the country list beside a phone number). */
    minListWidth?: number;
    /** Several choices: the list stays open, options toggle, chips show them under the box. */
    multiple?: boolean;
    /** With `multiple`: what is typed in the search and not in the list can be added as it is. */
    creatable?: boolean;
    /** With `multiple`: at most this many choices. */
    max?: number;
  }>(),
  { searchable: "auto" },
);
/** Above this many options the list would scroll, so a search box is added automatically. */
const SEARCH_THRESHOLD = 8;
const open = ref(false),
  root = ref<HTMLElement>(),
  trigger = ref<HTMLButtonElement>(),
  popup = ref<HTMLElement>(),
  search = ref<HTMLInputElement>(),
  query = ref("");
const searchable = computed(() =>
  props.searchable === "auto"
    ? props.options.length > SEARCH_THRESHOLD
    : props.searchable,
);
const fold = (v: string) =>
  v
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
const same = (a: string, b: string) => fold(a) === fold(b);
/** Chosen values that are not options (typed in) are listed too, so they can be unchecked there. */
const listed = computed(() =>
  props.multiple
    ? [
        ...props.options,
        ...values.value.filter((v) => !props.options.some((o) => same(o, v))),
      ]
    : props.options,
);
const filtered = computed(() => {
  const q = fold(query.value.trim());
  return q ? listed.value.filter((o) => fold(o).includes(q)) : listed.value;
});
const chosen = (opt: string) =>
  props.multiple ? values.value.some((v) => same(v, opt)) : model.value === opt;
const typed = computed(() => query.value.trim());
const canCreate = computed(
  () =>
    !!props.multiple &&
    !!props.creatable &&
    !!typed.value &&
    !listed.value.some((o) => same(o, typed.value)),
);
const full = computed(
  () => props.max !== undefined && values.value.length >= props.max,
);
function toggleValue(opt: string) {
  if (chosen(opt)) values.value = values.value.filter((v) => !same(v, opt));
  else if (!full.value) values.value = [...values.value, opt];
}
const removeValue = (v: string) =>
  (values.value = values.value.filter((x) => x !== v));
const MAX_HEIGHT = computed(() => (searchable.value ? 320 : 240));
const popupId = useId();
const position = ref<CSSProperties>({
  left: "0px",
  top: "0px",
  width: "200px",
  maxHeight: "240px",
  visibility: "hidden",
});
useEscapeLayer(open, () => close(true));
function close(restoreFocus = false) {
  if (popup.value?.matches(":popover-open")) popup.value.hidePopover();
  open.value = false;
  if (restoreFocus) trigger.value?.focus({ preventScroll: true });
}
function place() {
  if (!open.value || !trigger.value || !popup.value) return;
  const anchor = trigger.value.getBoundingClientRect();
  const gap = 6,
    margin = 8;
  const below = Math.max(0, innerHeight - anchor.bottom - gap - margin);
  const above = Math.max(0, anchor.top - gap - margin);
  // Inline, the box is only as wide as its value: the list takes the width of its longest option.
  if (props.inline) popup.value.style.width = "max-content";
  const width = props.inline
    ? Math.min(
        Math.max(anchor.width, popup.value.getBoundingClientRect().width),
        innerWidth - margin * 2,
      )
    : Math.min(
        Math.max(anchor.width, props.minListWidth ?? 0),
        innerWidth - margin * 2,
      );
  popup.value.style.width = width + "px";
  const naturalHeight = Math.min(
    MAX_HEIGHT.value,
    popup.value.scrollHeight + 2,
  );
  const upwards = below < naturalHeight && above > below;
  const maxHeight = Math.min(MAX_HEIGHT.value, upwards ? above : below);
  const height = Math.min(naturalHeight, maxHeight);
  position.value = {
    left:
      Math.max(margin, Math.min(anchor.left, innerWidth - width - margin)) +
      "px",
    top:
      Math.max(
        margin,
        upwards ? anchor.top - gap - height : anchor.bottom + gap,
      ) + "px",
    width: width + "px",
    maxHeight: maxHeight + "px",
    visibility: "visible",
  };
}
function outside(e: PointerEvent) {
  if (!root.value?.contains(e.target as Node)) close();
}
async function toggle() {
  if (open.value) {
    close(true);
    return;
  }
  if (props.disabled) return;
  position.value.visibility = "hidden";
  query.value = "";
  open.value = true;
  await nextTick();
  if (!open.value || !popup.value) return;
  // The top layer escapes modal clipping while retaining DOM/focus ownership.
  popup.value.showPopover();
  place();
  await nextTick();
  if (searchable.value) {
    search.value?.focus({ preventScroll: true });
    popup.value
      ?.querySelector<HTMLElement>("[aria-selected=true]")
      ?.scrollIntoView({ block: "nearest" });
    return;
  }
  popup.value
    ?.querySelector<HTMLElement>("[aria-selected=true], [role=option]")
    ?.focus({ preventScroll: true });
}
function pick(opt: string) {
  if (props.multiple) {
    // Several choices: the list stays open for the next one.
    toggleValue(opt);
    query.value = "";
    if (searchable.value) search.value?.focus({ preventScroll: true });
    return;
  }
  model.value = opt;
  close(true);
}
function create() {
  if (!canCreate.value || full.value) return;
  values.value = [...values.value, typed.value];
  query.value = "";
  search.value?.focus({ preventScroll: true });
}
/** Enter in the search box takes the first match (or adds what was typed); typing keeps the list in place. */
function searchKey(e: KeyboardEvent) {
  if (e.key === "Enter") {
    e.preventDefault();
    const first = filtered.value[0];
    if (first !== undefined) pick(first);
    else if (canCreate.value) create();
  }
}
watch([query, values], () => void nextTick(place));
function key(e: KeyboardEvent) {
  if (e.key === "Tab" && open.value) close(true);
  if (["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) {
    e.preventDefault();
    if (!open.value) {
      toggle();
      return;
    }
    const a = [
      ...(popup.value?.querySelectorAll<HTMLElement>("[role=option]") || []),
    ];
    const i = a.indexOf(document.activeElement as HTMLElement);
    const index =
      e.key === "Home"
        ? 0
        : e.key === "End"
          ? a.length - 1
          : (i + (e.key === "ArrowDown" ? 1 : -1) + a.length) % a.length;
    a[index]?.focus({ preventScroll: true });
    a[index]?.scrollIntoView({ block: "nearest" });
  }
}
watch(
  () => props.disabled,
  (disabled) => {
    if (disabled) close();
  },
);
onMounted(() => {
  document.addEventListener("pointerdown", outside);
  window.addEventListener("resize", place);
  window.addEventListener("scroll", place, true);
});
onBeforeUnmount(() => {
  document.removeEventListener("pointerdown", outside);
  window.removeEventListener("resize", place);
  window.removeEventListener("scroll", place, true);
});
</script>
<template>
  <div ref="root" class="wa-select" :class="{ inline }" @keydown="key">
    <button
      ref="trigger"
      type="button"
      class="select-trigger"
      :class="{ open, empty: multiple ? !values.length : !model }"
      :aria-label="label"
      aria-haspopup="listbox"
      :aria-expanded="open"
      :aria-controls="open ? popupId : undefined"
      :disabled="disabled"
      @click="toggle"
    >
      <span class="select-text">
        <span v-if="!inline" class="select-label">{{ label }}</span>
        <span class="select-value">{{
          multiple
            ? t("select.chosen", { count: values.length })
            : display && model
              ? display(model)
              : model
        }}</span>
      </span>
      <Icon name="down" :size="20" class="select-arrow" />
    </button>
    <div
      v-if="open"
      :id="popupId"
      ref="popup"
      popover="manual"
      class="select-options"
      role="listbox"
      :aria-label="label"
      :aria-multiselectable="multiple || undefined"
      :style="position"
    >
      <div v-if="searchable" class="select-search">
        <Icon name="search" :size="18" />
        <input
          ref="search"
          v-model="query"
          type="search"
          :placeholder="t('select.search')"
          :aria-label="t('select.search')"
          autocomplete="off"
          @keydown="searchKey"
        />
      </div>
      <button
        v-if="canCreate"
        role="option"
        aria-selected="false"
        class="select-create"
        :disabled="full"
        @click="create"
      >
        <Icon name="plus" :size="17" />{{ t("select.add", { value: typed }) }}
      </button>
      <button
        v-for="opt in filtered"
        :key="opt"
        role="option"
        :aria-selected="chosen(opt)"
        :disabled="multiple && full && !chosen(opt)"
        @click="pick(opt)"
      >
        {{ opt }}<Icon v-if="chosen(opt)" name="check" :size="17" />
      </button>
      <p
        v-if="searchable && !filtered.length && !canCreate"
        class="select-empty"
      >
        {{ t("select.empty") }}
      </p>
    </div>
    <div v-if="multiple && values.length" class="select-chips">
      <span v-for="v in values" :key="v" class="chips-input-chip"
        >{{ v
        }}<button
          type="button"
          :aria-label="t('chips.remove', { value: v })"
          :disabled="disabled"
          @click="removeValue(v)"
        >
          <Icon name="close" :size="14" /></button
      ></span>
    </div>
  </div>
</template>
